// @givmo/design-system — Sheet (side-anchored panel over Base UI Dialog).
//
// SaaS overlay tier. A full-height panel anchored to a viewport edge — the drawer/inspector
// idiom the suite apps reach for when a surface needs its own working area beside the page
// rather than on top of it (Stripe's payment detail rail, Linear's issue panel).
//
// It is the same Base UI Dialog machinery <Dialog> uses — role="dialog", focus trap + restore,
// Escape and outside-press dismissal, `aria-labelledby`/`aria-describedby` wired from
// <Sheet.Title>/<Sheet.Description> — anchored and animated differently. What a Sheet adds over
// a centred Dialog is a LAYOUT CONTRACT: <Sheet.Header> and <Sheet.Footer> stay put while
// <Sheet.Body> scrolls, so a panel taller than the viewport keeps its title and its actions
// reachable. (The centred <Dialog> solves the same problem by clamping and scrolling the whole
// card; an edge-anchored panel is always exactly viewport-tall, so it splits instead.)
//
// ── Modality: three real modes, and why the scrim is derived rather than configurable ────────
// Base UI's `modal` is a THREE-way, and all three are honest here:
//   true          — focus trapped, page scroll locked, outside pointers disabled.
//   'trap-focus'  — focus trapped; page still scrolls and outside pointers still work.
//   false         — the page stays fully interactive; the panel is just a panel.
// `false` is what a working companion panel wants (keep the page usable while the panel is
// open). Such a panel also wants to survive a click on the page — and Base UI closes a NON-modal
// dialog when focus leaves it, so pair it with `disablePointerDismissal` (see the story).
//
// The scrim is therefore NOT a prop: it renders if and only if `modal` is `true`. A backdrop is
// a real full-viewport element with no pointer-events handling of its own, so drawing one over a
// non-modal or `trap-focus` sheet would block exactly the outside interaction those modes exist
// to permit — the panel would look blocking, behave blocking, and contradict its own mode. That
// is an invariant, not a preference, so it lives in code rather than in a caller's hands.
//
// `aria-modal` follows the root the same way. Base UI 1.6 writes `role="dialog"` on the popup but
// never `aria-modal` (see dialog.tsx for the verified detail and why the kit, not Base UI, owns
// the attribute), so <Sheet.Content> derives it from the root's `modal`: `true` and `'trap-focus'`
// both take the page out of reach and are announced modal; `false` — the working companion panel —
// carries no `aria-modal` at all, because a panel that leaves the page interactive must not tell a
// screen reader the page is off-limits.
//
// ── The two animation traps this file is written around ──────────────────────────────────────
// Both are documented in agent_docs/build_and_ci.md ("Tailwind v4 utility traps") and both have
// shipped as real defects in this repo's other off-canvas surface:
//   1. Tailwind 4 compiles the off-canvas x-translate utility (the variant forms in `SIDE`,
//      below) to the STANDALONE `translate` property, not to `transform`. A transition naming
//      the transform property instead would name a property nothing writes: the panel would snap
//      open with the class list still reading exactly as intended. Verified against the pinned
//      tailwindcss 4.3.1 — that utility emits
//      `translate: var(--tw-translate-x) var(--tw-translate-y)`. Hence `transition-[translate]`.
//      (Neither that utility nor the transform-named transition is spelled out here, on purpose:
//      Tailwind scans source as plain TEXT, comments included, so naming either in prose emits
//      its rule into every consuming app's bundle with nothing on the page using it. The bare
//      x-translate selector is unused by this file — `SIDE` only ever writes the
//      `data-[starting-style]:`/`data-[ending-style]:` variant forms.)
//   2. The open state uses `translate-none`, not the `-0` form. Both paint identically, but the
//      `-0` form sets a translate VALUE, which makes the panel a containing block for any
//      `position: fixed` descendant. `translate-none` leaves no value at all. (It still animates:
//      `none` is the identity for `translate` interpolation, so the slide runs either way.)
// `motion-reduce:transition-none` drops both the slide and the scrim fade for a reader who asked
// for less motion; Base UI then unmounts on the next frame instead of waiting on an animation.
import { createContext, useContext, type ReactNode } from 'react';
import {
  Dialog as BaseDialog,
  type DialogPopupProps,
  type DialogRootProps,
} from '@base-ui/react/dialog';
import { X } from 'lucide-react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { Icon } from './icon';
import { IconButton } from './icon-button';

export type SheetSide = 'right' | 'left' | 'bottom';
export type SheetSize = 'sm' | 'md' | 'lg';

/**
 * Root-supplied modality + the Content's `testId`, so <Sheet.Content> can decide whether a scrim
 * is honest and <Sheet.Header> can derive its close id the way <PopUpTemplate> does.
 */
interface SheetContextValue {
  modal: boolean | 'trap-focus';
  testId?: string;
}

const SheetContext = createContext<SheetContextValue>({ modal: true });

export interface SheetRootProps extends Pick<
  DialogRootProps,
  'open' | 'defaultOpen' | 'onOpenChange' | 'onOpenChangeComplete' | 'disablePointerDismissal'
> {
  children: ReactNode;
  /**
   * Modality (default `true`). `'trap-focus'` keeps focus in the panel but leaves the page
   * scrollable and clickable; `false` leaves the page fully interactive. The scrim follows —
   * only `true` draws one.
   */
  modal?: boolean | 'trap-focus';
}

/**
 * Groups the parts and owns modality. Renders no DOM of its own.
 *
 * Unlike `<Dialog.Root>` (a bare Base UI re-export) this is a thin wrapper, because `modal` has
 * to reach `<Sheet.Content>` for the scrim invariant above.
 */
function SheetRoot({ children, modal = true, ...props }: SheetRootProps) {
  return (
    <BaseDialog.Root modal={modal} {...props}>
      <SheetContext.Provider value={{ modal }}>{children}</SheetContext.Provider>
    </BaseDialog.Root>
  );
}

const SheetTrigger = BaseDialog.Trigger;
const SheetClose = BaseDialog.Close;
const SheetTitle = BaseDialog.Title;
const SheetDescription = BaseDialog.Description;

/**
 * Panel width per size. Arbitrary values deliberately: the theme's spacing scale is sparse and
 * carries nothing at panel width, and a bare numeric utility there would silently resolve
 * through Tailwind's own 4px base (`check:spacing` guards exactly this).
 *
 * `max-w-full` rather than a gutter clamp — an edge-anchored panel is flush with the viewport
 * edge by definition, so on a narrow screen it should become full-bleed, not float inside a
 * margin.
 */
const SIZE: Record<SheetSize, string> = {
  sm: 'w-[360px] max-w-full',
  md: 'w-[440px] max-w-full',
  lg: 'w-[560px] max-w-full',
};

/**
 * Anchor + slide, per side. The off-canvas offset is carried on Base UI's `data-starting-style`
 * (animating in) and `data-ending-style` (animating out); the resting state is `translate-none`.
 * Full class strings, never assembled — Tailwind scans source as text and would emit nothing for
 * a built-up name.
 */
const SIDE: Record<SheetSide, string> = {
  right:
    'right-0 border-l data-[starting-style]:translate-x-full data-[ending-style]:translate-x-full',
  left: 'left-0 border-r data-[starting-style]:-translate-x-full data-[ending-style]:-translate-x-full',
  // The bottom sheet — the phone treatment of a form that is a centred dialog on a wide screen: full
  // width, content-tall up to 90dvh (the base `top-0 h-dvh` and the width from SIZE are overridden
  // here, later classes winning through tailwind-merge), rounded top corners, sliding up.
  bottom:
    'inset-x-0 top-auto bottom-0 h-auto max-h-[90dvh] w-full rounded-t-[20px] border-t data-[starting-style]:translate-y-full data-[ending-style]:translate-y-full',
};

/**
 * The sheet dims itself while a dialog opened from inside it is open.
 *
 * Base UI draws no backdrop for a NESTED dialog (its backdrop is enabled only for a top-level one) and
 * marks the parent popup `data-nested-dialog-open` instead. Without this the page sat under the
 * sheet's scrim while the sheet itself stayed bright beside the dialog — two depths for one modal
 * layer (CCM-R1-INTAKE (a); DESIGN-NOTE-1b round 5, M2). The overlay covers the sheet's own 1px
 * border too (`-inset-px`: an absolute box spans the padding box only, so the border would stay a
 * bright seam), and takes no pointer events: the dialog above it owns the input.
 */
export const NESTED_DIALOG_DIM = [
  'data-[nested-dialog-open]:after:pointer-events-none',
  'data-[nested-dialog-open]:after:absolute',
  'data-[nested-dialog-open]:after:-inset-px',
  'data-[nested-dialog-open]:after:z-[1]',
  'data-[nested-dialog-open]:after:bg-scrim',
].join(' ');

export interface SheetContentProps extends TestIdProps {
  children: ReactNode;
  /**
   * Viewport edge the panel is anchored to (default `'right'`). `'bottom'` is the bottom sheet —
   * full width, content-tall (up to 90dvh), sliding up — the phone treatment of a dialog.
   */
  side?: SheetSide;
  /** Panel width: `sm` 360px, `md` 440px, `lg` 560px — each capped at the viewport (default `md`). */
  size?: SheetSize;
  /** Extra classes merged onto the panel. */
  className?: string;
  /**
   * Element to focus when the sheet opens. Base UI's default is the first tabbable element,
   * which in a headered panel is the close button — pass a ref to focus the real entry point.
   */
  initialFocus?: DialogPopupProps['initialFocus'];
  /** Element to focus on close (default: the trigger). */
  finalFocus?: DialogPopupProps['finalFocus'];
  /** Accessible name. Unnecessary when the panel renders a <Sheet.Header title> or <Sheet.Title>. */
  'aria-label'?: string;
}

/**
 * Portal + (modal-only) scrim + the anchored panel, in one part.
 *
 * The panel is a full-height flex column so <Sheet.Header>/<Sheet.Footer> hold their edges while
 * <Sheet.Body> takes the remaining space and scrolls. Height is the DYNAMIC viewport unit:
 * mobile browser chrome changes the visible height, and a static unit would run the footer under
 * it. Children other than the three regions are laid out by the caller — this is a shell, not a
 * template.
 */
function SheetContent({
  children,
  side = 'right',
  size = 'md',
  className,
  initialFocus,
  finalFocus,
  testId,
  'aria-label': ariaLabel,
}: SheetContentProps) {
  const { modal } = useContext(SheetContext);
  return (
    <BaseDialog.Portal>
      {modal === true && (
        // The panel's own `z-50`, as `Dialog.Content`'s scrim shares its card's: portal order lays this
        // scrim over a dialog card the sheet opened from, which dims and takes no clicks.
        <BaseDialog.Backdrop
          className={cn(
            'fixed inset-0 z-50 bg-scrim',
            'transition-[opacity] duration-200 ease-out motion-reduce:transition-none',
            'data-[starting-style]:opacity-0 data-[ending-style]:opacity-0',
          )}
        />
      )}
      <BaseDialog.Popup
        aria-label={ariaLabel}
        aria-modal={modal === false ? undefined : 'true'}
        initialFocus={initialFocus}
        finalFocus={finalFocus}
        className={cn(
          'fixed top-0 z-50 flex h-dvh flex-col',
          'border-border-subtle bg-surface shadow-xl outline-none',
          'translate-none transition-[translate] duration-200 ease-out motion-reduce:transition-none',
          NESTED_DIALOG_DIM,
          SIZE[size],
          SIDE[side],
          className,
        )}
        {...testIdProp(testId)}
      >
        <SheetContext.Provider value={{ modal, testId }}>{children}</SheetContext.Provider>
      </BaseDialog.Popup>
    </BaseDialog.Portal>
  );
}

export interface SheetHeaderProps extends TestIdProps {
  /** Panel title. Rendered as <Sheet.Title>, so it becomes the sheet's accessible name. */
  title?: ReactNode;
  /** Supporting line under the title. Rendered as <Sheet.Description>. */
  description?: ReactNode;
  /** Render the trailing close button (default `true`). */
  showClose?: boolean;
  /** Replaces the title/description block entirely. */
  children?: ReactNode;
  className?: string;
}

/**
 * Fixed top region: title, optional description, and the close affordance.
 *
 * The close button is on by default and should stay on — Base UI's own guidance is to render a
 * `<Dialog.Close>` inside the popup whenever focus is trapped, so a touch screen-reader user has
 * a way out. Its `data-testid` derives from the CONTENT's `testId` as `${testId}-close`, the same
 * derivation <PopUpTemplate> uses.
 */
function SheetHeader({
  title,
  description,
  showClose = true,
  children,
  className,
  testId,
}: SheetHeaderProps) {
  const { testId: contentTestId } = useContext(SheetContext);
  return (
    <div
      className={cn(
        'flex shrink-0 items-start justify-between gap-16 border-b border-border-subtle px-20 py-16',
        className,
      )}
      {...testIdProp(testId)}
    >
      {children ?? (
        <div className="min-w-0">
          {title != null && (
            <SheetTitle className="truncate font-sans text-s16 font-w600 tracking-givmo text-text-strong">
              {title}
            </SheetTitle>
          )}
          {description != null && (
            <SheetDescription className="mt-2 font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
              {description}
            </SheetDescription>
          )}
        </div>
      )}
      {showClose && (
        <SheetClose
          render={
            <IconButton
              size="sm"
              aria-label="Close"
              testId={contentTestId ? `${contentTestId}-close` : undefined}
            />
          }
        >
          <Icon icon={X} size={18} aria-hidden />
        </SheetClose>
      )}
    </div>
  );
}

export interface SheetBodyProps extends TestIdProps {
  children: ReactNode;
  /** Remove the default padding (for edge-to-edge lists). */
  flush?: boolean;
  className?: string;
}

/**
 * The scrolling middle region.
 *
 * `min-h-0` is load-bearing, not decoration: a flex child's default `min-height: auto` refuses to
 * shrink below its content, so without it a long body pushes the footer off the bottom of the
 * panel instead of scrolling. `overscroll-contain` keeps a touch scroll that reaches either end
 * from chaining to the page behind it.
 */
function SheetBody({ children, flush = false, className, testId }: SheetBodyProps) {
  return (
    <div
      className={cn(
        'min-h-0 flex-1 overflow-y-auto overscroll-contain',
        flush ? '' : 'p-20',
        className,
      )}
      {...testIdProp(testId)}
    >
      {children}
    </div>
  );
}

export interface SheetFooterProps extends TestIdProps {
  children: ReactNode;
  className?: string;
}

/** Fixed bottom region — the panel's action bar, trailing-aligned. */
function SheetFooter({ children, className, testId }: SheetFooterProps) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-end gap-8 border-t border-border-subtle px-20 py-14',
        className,
      )}
      {...testIdProp(testId)}
    >
      {children}
    </div>
  );
}

/** Compound Sheet API: `<Sheet.Root>…<Sheet.Trigger>…<Sheet.Content>…`. */
export const Sheet = {
  Root: SheetRoot,
  Trigger: SheetTrigger,
  Content: SheetContent,
  Header: SheetHeader,
  Body: SheetBody,
  Footer: SheetFooter,
  Close: SheetClose,
  Title: SheetTitle,
  Description: SheetDescription,
};
